"use client";

import type {PrimitiveIcon, UIIcon} from "@gluestack-ui/icon";
import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {useStyleContext, withStyleContext} from "@gluestack-ui/nativewind-utils/withStyleContext";
import React from "react";
import {View} from "react-native";

import {Text} from "../text";

import {badgeIconStyle, badgeStyle, badgeTextStyle} from "./styles";

const SCOPE = "BADGE";

const ContextView = withStyleContext(View, SCOPE);

type IBadgeProps = React.ComponentPropsWithoutRef<typeof ContextView> &
    VariantProps<typeof badgeStyle>;

const Badge = ({
    children,
    action = "default",
    variant = "solid",
    size = "sm",
    className,
    testID = "badge",
    ...props
}: {className?: string; testID?: string} & IBadgeProps) => {
    return (
        <ContextView
            className={badgeStyle({action, variant, size, class: className})}
            testID={testID ?? "badge"}
            {...props}
            context={{
                action,
                variant,
                size
            }}
        >
            {children}
        </ContextView>
    );
};

type IBadgeTextProps = React.ComponentPropsWithoutRef<typeof Text> &
    VariantProps<typeof badgeTextStyle> & {
        // prevent from passing the non-string children which is likely to cause errors
        children: string;
    };

const BadgeText = React.forwardRef<React.ElementRef<typeof Text>, IBadgeTextProps>(
    ({children, className, ...props}, ref) => {
        const {action: parentAction, variant: parentVariant} = useStyleContext(SCOPE);
        return (
            <Text
                ref={ref}
                className={badgeTextStyle({
                    parentVariants: {
                        action: parentAction,
                        variant: parentVariant
                    },
                    class: className
                })}
                {...props}
            >
                {children}
            </Text>
        );
    }
);

type IBadgeIconProps = React.ComponentPropsWithoutRef<typeof PrimitiveIcon> &
    VariantProps<typeof badgeIconStyle> & {
        as: React.ElementType;
    };

const BadgeIcon = React.forwardRef<React.ElementRef<typeof UIIcon>, IBadgeIconProps>(
    ({className, as: Component, ...props}, ref) => {
        const {action: parentAction, variant: parentVariant} = useStyleContext(SCOPE);
        return (
            <Component
                className={badgeIconStyle({
                    parentVariants: {
                        action: parentAction,
                        variant: parentVariant
                    },
                    class: className
                })}
                {...props}
                ref={ref}
            />
        );
    }
);

Badge.displayName = "Badge";
BadgeText.displayName = "BadgeText";
BadgeIcon.displayName = "BadgeIcon";

export {Badge, BadgeIcon, BadgeText};
